import { getUserFullName } from '@bliks-io/ui';
import {
  isSelectableDifference,
  withoutBrokenDependents,
  type DataSyncDifference,
  type DataSyncUnsuppliedReason,
  type DataSyncRun
} from '@bliks-io/contracts';

/** The steps that list differences, in wizard order; Bestätigen follows them. */
export const SYNC_STEPS = [
  {
    id: 'structure',
    confirmKey: 'dataSync.sync.confirm.structure',
    destructive: false
  },
  {
    id: 'newPeople',
    confirmKey: 'dataSync.sync.confirm.createPeople',
    destructive: false
  },
  {
    id: 'changes',
    confirmKey: 'dataSync.sync.confirm.changePeople',
    destructive: false
  },
  {
    id: 'leavers',
    confirmKey: 'dataSync.sync.confirm.deletePeople',
    destructive: true
  },
  {
    id: 'removals',
    confirmKey: 'dataSync.sync.confirm.deleteUnits',
    destructive: true
  }
] as const;

export type SyncStep = (typeof SYNC_STEPS)[number];

export type SyncStepId = SyncStep['id'] | 'confirm';

const STEP_OF_KIND: Record<DataSyncDifference['kind'], SyncStep['id']> = {
  departmentCreate: 'structure',
  departmentMove: 'structure',
  teamCreate: 'structure',
  roleCreate: 'structure',
  systemCreate: 'structure',
  unitRename: 'structure',
  personCreate: 'newPeople',
  personUpdate: 'changes',
  unitLead: 'changes',
  leaver: 'leavers',
  unitDelete: 'removals'
};

type PersonCreate = Extract<DataSyncDifference, { kind: 'personCreate' }>;

/** A new person blocked only by what the source lacks: listed on Bestätigen, not as a row. */
function isUnsupplied(
  difference: DataSyncDifference
): difference is PersonCreate & { blockedReason: 'missingDepartment' | 'missingRole' } {
  return (
    difference.kind === 'personCreate' &&
    (difference.blockedReason === 'missingDepartment' || difference.blockedReason === 'missingRole')
  );
}

export function differencesOfStep(differences: DataSyncDifference[], step: SyncStep) {
  return differences.filter(
    (difference) => STEP_OF_KIND[difference.kind] === step.id && !isUnsupplied(difference)
  );
}

export function stepRowCount(run: DataSyncRun, step: SyncStep) {
  return differencesOfStep(run.differences, step).length;
}

const UNSUPPLIED_REASON_KEY: Record<DataSyncUnsuppliedReason, string> = {
  missingDepartment: 'dataSync.sync.blockedReason.missingDepartment',
  missingRole: 'dataSync.sync.blockedReason.missingRole',
  missingEmail: 'dataSync.sync.skipped.missingEmail'
};

/** A person the source cannot supply, as the note lists them: by name, else by email. */
export function unsuppliedPerson(
  key: string,
  person: { firstName: string; lastName: string; email?: string | null },
  reasons: DataSyncUnsuppliedReason[]
) {
  return {
    key,
    name:
      person.firstName || person.lastName || !person.email
        ? getUserFullName(person.firstName, person.lastName)
        : person.email,
    reasonKeys: reasons.map((reason) => UNSUPPLIED_REASON_KEY[reason])
  };
}

export type UnsuppliedPerson = ReturnType<typeof unsuppliedPerson>;

/** The people the source cannot supply because their department, Stelle or email is missing. */
export function unsuppliedPeople(run: DataSyncRun): UnsuppliedPerson[] {
  return [
    ...run.differences
      .filter(isUnsupplied)
      .map((person) => unsuppliedPerson(person.id, person, [person.blockedReason])),
    ...run.skippedPeople.map((person, index) =>
      unsuppliedPerson(`skipped-${index}`, person, [person.reason])
    )
  ];
}

/** The steps with rows, then Bestätigen. */
export function visibleSteps(run: DataSyncRun): SyncStepId[] {
  return [
    ...SYNC_STEPS.filter((step) => stepRowCount(run, step) > 0).map(({ id }) => id),
    'confirm'
  ];
}

export function selectedCount(
  differences: DataSyncDifference[],
  selected: ReadonlySet<string>,
  step: SyncStep
) {
  return differencesOfStep(differences, step).filter(({ id }) => selected.has(id)).length;
}

/**
 * The steps holding the unselected differences a selectable-but-for-them
 * difference depends on; none when it is blocked or selectable.
 */
export function unselectedDependencySteps(
  difference: DataSyncDifference,
  byId: ReadonlyMap<string, DataSyncDifference>,
  selected: ReadonlySet<string>
): SyncStep['id'][] {
  if (difference.blockedReason !== null) return [];
  const steps = difference.dependsOn.flatMap((id) => {
    const dependency = byId.get(id);
    return dependency && !selected.has(id) ? [STEP_OF_KIND[dependency.kind]] : [];
  });
  return SYNC_STEPS.map(({ id }) => id).filter((id) => steps.includes(id));
}

/**
 * Selecting adds every selectable target, including those another target makes
 * selectable; unselecting also unselects every difference that depends on a
 * target, transitively.
 */
export function setChecked(
  differences: DataSyncDifference[],
  selected: ReadonlySet<string>,
  targets: DataSyncDifference[],
  checked: boolean
): ReadonlySet<string> {
  const result = new Set(selected);
  if (!checked) {
    for (const { id } of targets) result.delete(id);
    return withoutBrokenDependents(differences, result);
  }
  let added = true;
  while (added) {
    added = false;
    for (const target of targets) {
      if (!result.has(target.id) && isSelectableDifference(target, result)) {
        result.add(target.id);
        added = true;
      }
    }
  }
  return result;
}

/** A step's select-all checkbox over its currently selectable differences. */
export function selectAllState(
  selectable: DataSyncDifference[],
  selected: ReadonlySet<string>
): boolean | 'indeterminate' {
  const selectedCount = selectable.filter(({ id }) => selected.has(id)).length;
  if (selectedCount === 0) return false;
  return selectedCount === selectable.length ? true : 'indeterminate';
}

/** The differences an apply left failed or in conflict. */
export function notApplied(run: DataSyncRun) {
  return run.differences.filter(({ id }) => {
    const outcome = run.outcomes[id];
    return outcome === 'failed' || outcome === 'conflict';
  });
}

/** A retry writes the failed differences again; conflicts stay. */
export function canRetry(run: DataSyncRun) {
  return Object.values(run.outcomes).includes('failed');
}
